import type { ChangeEvent } from 'react';
import { useEffect } from 'react';

import { Box, Button, Checkbox, Container, FormControlLabel, Typography } from '@mui/material';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useRecoilValue } from 'recoil';

import { TrimTextField } from '../../../../components/base';
import PasswordField from '../../../../components/base/PasswordField';
import { localStorageKeys } from '../../../../shared/config';
import { getEnvironment, useLocalStorage } from '../../../../shared/lib';
import { authState, useLogin } from '../../../../state/auth';
import backgroundImage from '../../assets/background.jpg';
import logoImage from '../../assets/logo.png';

type LoginForm = {
  identifier: string;
  password: string;
};

const environment = getEnvironment();

export const Login = () => {
  const showDocuments = environment === 'international' || environment === 'development';

  const [accepted, setAccepted] = useLocalStorage(localStorageKeys.accessingPolicy, showDocuments);
  const navigate = useNavigate();
  const auth = useRecoilValue(authState);
  const { execute: login } = useLogin();
  const form = useForm<LoginForm>({
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = form;

  const onSubmit = ({ identifier, password }: LoginForm) => {
    login({ identifier, password });
  };

  const onAcceptedChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAccepted(e.target.checked);
  };

  useEffect(() => {
    if (auth) {
      navigate('/');
    }
  }, [auth, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <FormProvider {...form}>
        <Container
          maxWidth='xs'
          sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', flexGrow: 1 }}
          component='form'
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              component='img'
              src={logoImage}
              sx={{
                maxHeight: 200,
                height: '10.4vw',
                marginTop: '4.68vw',
                marginBottom: '2.5vw',
              }}
            />
            <Box
              sx={{ backgroundColor: 'white', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              borderRadius={2}
              padding={2}
            >
              <Typography variant='h4'>
                <Trans i18nKey='login' />
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Controller
                  name='identifier'
                  rules={{ required: true, validate: (value) => Boolean(value.trim()) }}
                  control={control}
                  render={({ field }) => (
                    <TrimTextField
                      autoFocus
                      autoComplete='email'
                      placeholder='Email Address'
                      label='Email Address'
                      margin='normal'
                      error={Boolean(errors.identifier)}
                      fullWidth
                      required
                      {...field}
                    />
                  )}
                />
                <Controller
                  name='password'
                  rules={{ required: true, validate: (value) => Boolean(value.trim()) }}
                  control={control}
                  render={({ field }) => (
                    <PasswordField
                      autoFocus
                      autoComplete='current-password'
                      type='password'
                      placeholder='Password'
                      label='Password'
                      margin='normal'
                      error={Boolean(errors.password)}
                      fullWidth
                      required
                      {...field}
                    />
                  )}
                />
                <FormControlLabel control={<Checkbox value='remember' color='primary' />} label='Remember me' />
                <Button type='submit' fullWidth variant='contained' sx={{ mt: 3, mb: 2, color: '#FFFFFF' }}>
                  <Trans i18nKey='login' />
                </Button>
              </Box>
              {showDocuments && (
                <Box display='flex' gap={2} mt={2}>
                  <FormControlLabel
                    control={
                      <Checkbox value='remember' color='primary' checked={accepted} onChange={onAcceptedChange} />
                    }
                    label={
                      <Typography color='GrayText'>
                        <Trans
                          i18nKey='accessing-policy-text'
                          components={[
                            <Typography component='a' color='GrayText' />,
                            <Typography component='a' color='GrayText' />,
                          ]}
                        />
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Container>
      </FormProvider>
    </Box>
  );
};
